/**
 * BYO PDF e-sign field helpers (ZRF-ESIGN-02 + merge keys ZRF-ESIGN-03).
 *
 * BE contract:
 * Field (pdf-lib points, origin bottom-left):
 *   { id, type, page, x, y, w, h, role?, signerEmail?, label?, required?, mergeKey? }
 *   aliases accepted: pageIndex, width, height
 *   type "merge" + mergeKey from ESIGN_MERGE_KEYS allowlist
 *
 * Persist: GET/PUT /api/e-signature/documents/:id/fields → { fields }
 * Create:  POST /api/e-signature/create-request + { fields?, byoPdf? }
 * Sign:    POST /api/e-signature/pdf/:id/sign
 *          { signerEmail, signatureImage, initialsImage?, fieldValues }
 * Merge values resolve server-side from document attach (property/tenant/lease); missing → blank.
 */

import api from '../api';

export const ESIGN_MERGE_KEYS = [
  { key: 'tenant.name', label: 'Tenant name' },
  { key: 'landlord.name', label: 'Landlord name' },
  { key: 'property.address', label: 'Property address' },
  { key: 'unit.label', label: 'Unit' },
  { key: 'lease.rent', label: 'Lease rent' },
  { key: 'lease.start', label: 'Lease start' },
  { key: 'lease.end', label: 'Lease end' }
];

/** Accept FE shorthand "unit" → canonical unit.label */
const MERGE_KEY_ALIASES = {
  unit: 'unit.label',
  'unit.number': 'unit.label',
  'unit.name': 'unit.label'
};

export const ESIGN_FIELD_TYPES = [
  { type: 'signature', label: 'Signature', defaultW: 180, defaultH: 48 },
  { type: 'initials', label: 'Initials', defaultW: 64, defaultH: 36 },
  { type: 'dateSigned', label: 'Date signed', defaultW: 110, defaultH: 28 },
  { type: 'name', label: 'Printed name', defaultW: 160, defaultH: 28 },
  { type: 'text', label: 'Text', defaultW: 160, defaultH: 28 },
  { type: 'checkbox', label: 'Acknowledgment', defaultW: 22, defaultH: 22 },
  { type: 'merge', label: 'Merge data', defaultW: 160, defaultH: 28 }
];

/** Default text when placing a checkbox (avoid bare "Checkbox" on the sign form). */
export const DEFAULT_CHECKBOX_LABEL = 'I agree / acknowledge';

export function newFieldId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `fld-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function fieldTypeMeta(type) {
  return ESIGN_FIELD_TYPES.find((t) => t.type === type) || ESIGN_FIELD_TYPES[0];
}

export function mergeKeyMeta(key) {
  const canonical = canonicalizeMergeKey(key);
  return ESIGN_MERGE_KEYS.find((k) => k.key === canonical) || null;
}

export function canonicalizeMergeKey(key) {
  const raw = String(key || '').trim();
  if (!raw) return '';
  if (MERGE_KEY_ALIASES[raw]) return MERGE_KEY_ALIASES[raw];
  return raw;
}

export function isMergeField(field) {
  if (!field) return false;
  if (field.type === 'merge') return true;
  return Boolean(canonicalizeMergeKey(field.mergeKey));
}

/** Screen (top-left) → PDF (bottom-left) points for a given page size. */
export function screenRectToPdf({ left, top, width, height }, pageWidth, pageHeight, scale) {
  const x = left / scale;
  const w = width / scale;
  const h = height / scale;
  const yTop = top / scale;
  const y = pageHeight - yTop - h;
  return {
    x: round2(x),
    y: round2(y),
    width: round2(w),
    height: round2(h)
  };
}

/** PDF (bottom-left) → screen (top-left) CSS px. */
export function pdfRectToScreen(field, pageHeight, scale) {
  const left = (field.x || 0) * scale;
  const width = (field.width || field.w || 0) * scale;
  const height = (field.height || field.h || 0) * scale;
  const top = (pageHeight - (field.y || 0) - (field.height || field.h || 0)) * scale;
  return { left, top, width, height };
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/** Normalize API/BE field → FE UI shape (pageIndex, width, height). */
export function normalizeEsignField(raw = {}) {
  const mergeKey = canonicalizeMergeKey(raw.mergeKey);
  const typeRaw = String(raw.type || (mergeKey ? 'merge' : 'signature'));
  const type = mergeKey && typeRaw !== 'merge' ? 'merge' : typeRaw;
  const meta = fieldTypeMeta(type === 'merge' ? 'merge' : type);
  const page = Number(raw.page ?? raw.pageIndex);
  const width = Number(raw.w ?? raw.width);
  const height = Number(raw.h ?? raw.height);
  const keyMeta = mergeKey ? mergeKeyMeta(mergeKey) : null;
  return {
    id: raw.id || newFieldId(),
    type: type === 'merge' || mergeKey ? 'merge' : meta.type,
    pageIndex: Number.isFinite(page) ? page : 0,
    x: Number(raw.x) || 0,
    y: Number(raw.y) || 0,
    width: Number.isFinite(width) && width > 0 ? width : meta.defaultW,
    height: Number.isFinite(height) && height > 0 ? height : meta.defaultH,
    role: raw.role || '',
    signerEmail: raw.signerEmail || '',
    label: raw.label || keyMeta?.label || meta.label,
    required: type === 'merge' || mergeKey ? false : raw.required !== false,
    mergeKey: mergeKey || undefined
  };
}

/** FE UI field → BE persist shape. */
export function toApiEsignField(field) {
  const n = normalizeEsignField(field);
  const out = {
    id: n.id,
    type: n.type,
    page: n.pageIndex,
    x: n.x,
    y: n.y,
    w: n.width,
    h: n.height,
    role: n.role || undefined,
    signerEmail: n.signerEmail || undefined,
    label: n.label || undefined,
    required: n.type === 'merge' ? false : n.required !== false
  };
  if (n.type === 'merge' || n.mergeKey) {
    out.type = 'merge';
    out.mergeKey = n.mergeKey || undefined;
  }
  return out;
}

export async function fetchDocumentEsignFields(documentId) {
  const res = await api.get(`/api/e-signature/documents/${documentId}/fields`);
  const fields = Array.isArray(res.data?.fields) ? res.data.fields : [];
  return fields.map(normalizeEsignField);
}

export async function saveDocumentEsignFields(documentId, fields) {
  const payload = {
    fields: (fields || []).map(toApiEsignField)
  };
  const res = await api.put(`/api/e-signature/documents/${documentId}/fields`, payload);
  const saved = Array.isArray(res.data?.fields) ? res.data.fields : payload.fields;
  return saved.map(normalizeEsignField);
}

/** Fields assigned to this signer (email and/or role), or unassigned (any signer). */
export function fieldsForSigner(fields, { email, role } = {}) {
  const list = Array.isArray(fields) ? fields : [];
  const e = String(email || '')
    .trim()
    .toLowerCase();
  const r = String(role || '')
    .trim()
    .toLowerCase();
  return list.filter((f) => {
    const fe = String(f.signerEmail || '')
      .trim()
      .toLowerCase();
    const fr = String(f.role || '')
      .trim()
      .toLowerCase();
    if (!fe && !fr) return true;
    if (fe && e && fe === e) return true;
    if (fr && r && fr === r) return true;
    return false;
  });
}

/** Signer ink/text UI — exclude system merge fields (filled at stamp). */
export function signerCollectableFields(fields, signer) {
  return fieldsForSigner(fields, signer).filter((f) => !isMergeField(f));
}

export function isInkFieldType(type) {
  return type === 'signature' || type === 'initials';
}

/**
 * Human-facing label for placer overlays and the public sign form.
 * Upgrades legacy bare "Checkbox" labels so signers know what they're confirming.
 */
export function fieldDisplayLabel(field, { indexAmongType } = {}) {
  if (!field) return 'Field';
  if (isMergeField(field)) {
    return mergeKeyMeta(field.mergeKey)?.label || field.label || field.mergeKey || 'Merge data';
  }
  const raw = String(field.label || '').trim();
  const type = String(field.type || '');
  const who = String(field.role || '').trim();
  if (type === 'checkbox') {
    const bare =
      !raw ||
      /^checkbox$/i.test(raw) ||
      /^acknowledgment$/i.test(raw);
    if (!bare) return raw;
    const n =
      Number.isFinite(indexAmongType) && indexAmongType >= 0 ? indexAmongType + 1 : null;
    const base = n != null ? `Acknowledgment ${n}` : DEFAULT_CHECKBOX_LABEL;
    return who ? `${base} (${who})` : base;
  }
  if (raw) return raw;
  return fieldTypeMeta(type).label || type || 'Field';
}
